import { useEffect, useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from "motion/react";
import Search from './components/Search';
import Carousel from './components/Carousel';
import { useAppStore } from './stores/useAppStore';
import Logo from './components/Logo';
import IconSearch from './components/IconSearch';
import IconClose from './components/IconClose';
import ErrorBoundary from './components/ErrorBoundary';
import AnimatedGradientLayer from './components/AnimatedGradientLayer';
import OrientationLock from './components/OrientationLock';
import InstallPWA from './components/InstallPWA';
import { useResponsiveFontSize } from './hooks/useResponsiveFontSize';

import { CAROUSEL_TYPES } from './constants/carouselTypes';
import PageKeyDemos from './pages/PageKeyDemos';
import CardKeyDemoBig from './components/CardKeyDemoBig';
import Viewer from './components/Viewer';
import Landing from './components/Landing';
import { useInactivityDetector } from './hooks/useInactivityDetector';
import { useDoubleClick } from './hooks/useDoubleClick';
import HomeQR from './components/HomeQR';

const MotionIconSearch = motion.create(IconSearch);
const MotionIconClose = motion.create(IconClose);
const MotionLogo = motion.create(Logo);
const MotionButton = motion.create('button');

function App() {
  const location = useLocation();
  const { isOpen: carouselIsOpen, currentIndex: carouselIndex, type: carouselType, closeCarousel } = useAppStore((state) => state.carousel);
  const { isOpen: viewerIsOpen, currentProduct: viewerProduct, closeViewer } = useAppStore((state) => state.viewer);
  const { content, menuItems, isLoading, loadContent } = useAppStore();
  const { fullScreen, toggleFullScreen, menuActiveKey, setMenuActiveKey, setCardTouchPos, homeQRIsOpen, setHomeQRIsOpen } = useAppStore();
  const [ prevLocation, setPrevLocation ] = useState(null);
  const { searchIsOpen, setSearchIsOpen } = useAppStore();
  const [ showLanding, setShowLanding ] = useState(true);
  const [ landingElementsVisible, setLandingElementsVisible ] = useState(true);

  // Detección de inactividad (5 minutos = 300000ms)
  // Solo se activa después de cerrar el landing inicial
  const isInactive = useInactivityDetector(300000, !showLanding);
  const [ showLandingByInactivity, setShowLandingByInactivity ] = useState(false);

  // Hook para ajustar el tamaño de fuente según el ancho de la pantalla
  useResponsiveFontSize(8, 1920);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      const handleContextMenu = (event) => {
        event.preventDefault();
      };

      // document.addEventListener('contextmenu', handleContextMenu);

      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, []);

  const componentsMap = {
    lets_talk_strategy: PageKeyDemos,
  };

  useEffect(() => {
    setSearchIsOpen(false);
  }, []);

  useEffect(() => {
    setPrevLocation(location);
  }, [location]);

  // Cargar el contenido JSON al montar el componente
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  useEffect(() => {
    if (!isLoading) setMenuActiveKey(menuItems[0].key);
  }, [isLoading]);

  // Efecto para mostrar Landing cuando hay inactividad
  useEffect(() => {
    if (isInactive && !showLanding) {
      showLandingScreen();
    }
  }, [isInactive, showLanding]);

  // Función para mostrar el Landing y resetear el estado de la app
  const showLandingScreen = () => {
    // Cerrar elementos y navegar a homepage después de un delay
    setTimeout(() => {
      closeCarousel();
      setSearchIsOpen(false);
    
    }, 1000);

    // Cerrar HomeQR si está abierto
    if (homeQRIsOpen) {
      setHomeQRIsOpen(false);
    }

    // Mostrar Landing
    setLandingElementsVisible(true);
    setShowLandingByInactivity(true);
  };

  const handleLandingClick = () => {
    // Primero desvanece los elementos internos
    setLandingElementsVisible(false);
    // Después de 0.6s, desvanece toda la capa
    setTimeout(() => {
      setShowLanding(false);
      setShowLandingByInactivity(false);
    }, 600);
  };

  // Handler para doble click/tap en el logo
  const handleLogoDoubleClick = useDoubleClick(() => {
    showLandingScreen();
  });

  const handleButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    let x, y;

    // Para eventos táctiles, usar changedTouches en lugar de touches
    if (event.changedTouches && event.changedTouches.length > 0) {
      x = event.changedTouches[0].clientX;
      y = event.changedTouches[0].clientY;
    } else if (event.touches && event.touches.length > 0) {
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    } else {
      // Evento de ratón
      x = event.clientX;
      y = event.clientY;
    }
    setCardTouchPos({x, y});

    /*
    if (fullScreen) toggleFullScreen();
    else if (viewerIsOpen) closeViewer();
    else if (carouselIsOpen) closeCarousel();
    else setSearchIsOpen(!searchIsOpen);
    */
    if (fullScreen) toggleFullScreen();
    else setSearchIsOpen(!searchIsOpen);
  };

  if (isLoading) {
    return <div></div>;
  }

  return (
    <ErrorBoundary>
      <OrientationLock />
      <div className="select-none relative min-h-screen flex flex-col w-full bg-cover bg-center bg-no-repeat bg-fixed">
        
        {/* Capa de degradado animado en la parte inferior */}
        <AnimatedGradientLayer />
        
        {/* Overlay opcional para mejorar la legibilidad */}
        <div className="absolute inset-0 bg-black bg-opacity-20 pointer-events-none"></div>
        <div className="w-full flex flex-1 relative z-10">
          {/*<Sidebar items={menuItems} />*/}

          <div className="w-full flex-1 min-w-0">

            <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                <Route
                  path="/"
                  element={<Navigate to={`/${menuItems[0]?.key}`} replace />}
                />
                {menuItems.map((item) => {
                  const Component = componentsMap[item.key];
                  if (!Component) {
                    console.error(`No se encontró un componente para la clave: ${item.key}`);
                    return null;
                  }
                  return (
                    <Route
                      key={item.key}
                      path={item.key}
                      element={
                        <ErrorBoundary>
                          <Component />
                        </ErrorBoundary>
                      }
                    />
                  );
                })}
              </Routes>
            </AnimatePresence>
          </div>
        </div>
      
      <AnimatePresence>
        {carouselIsOpen && carouselType === CAROUSEL_TYPES.KEY_DEMOS && (
          <Carousel
            key="lets_talk_strategy_carousel"
            title=""
            items={content?.lets_talk_strategy?.items}
            initialSlide={carouselIndex}
            itemWidth="169.125rem"
            itemHeight="73.125rem"
            SlideComponent={CardKeyDemoBig}
            prevButtonText="Previous Demo"
            nextButtonText="Next Demo"
            section="lets_talk_strategy"
          />
        )}

        {viewerIsOpen && (
          <Viewer key="product_viewer" item={viewerProduct} />
        )}

        {searchIsOpen && <Search key="search_layer" items={content.demos} />}
      </AnimatePresence>
      
      <MotionLogo 
        className="absolute w-[17.625rem] h-auto left-[8.25rem] top-[5.625rem] z-50" 
        animate={{ 
          opacity: fullScreen ? 0 : 1,
          transition: { duration: 0.3, ease: "easeOut" }
        }}
        style={{ pointerEvents: fullScreen ? 'none' : 'auto' }}
        onClick={handleLogoDoubleClick}
      />
      
      <MotionButton
        onClick={(e) => handleButtonClick(e)}
        className="click-effect absolute top-[6.625rem] right-[6.625rem] w-[9.75rem] h-[9.75rem] z-50 justify-center rounded-full focus:outline-none text-white text-[2rem] font-light leading-none flex items-center gap-[3.25rem] overflow-hidden"
        animate={{
          backgroundColor: !searchIsOpen && !fullScreen ? "#1a2951" : "#ffffff"
        }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        { !searchIsOpen && !fullScreen
          ? <MotionIconSearch
              key="icon_search"
              initial={{ x: "-200%" }}
              animate={{ x: 0 }}
              exit={{ y: "-200%" }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="w-[3.5rem] h-auto" />
          : <MotionIconClose
              key="icon_close"
              initial={{ x: "200%" }}
              animate={{ x: 0 }}
              exit={{ x: "200%" }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="w-[3.5rem] h-auto"
              color="#1C2C58"
            />
        }
      </MotionButton>
      
      {/* Componente para promover la instalación de la PWA */}
      <InstallPWA />

      {/* Landing Component - Pantalla completa */}
      <AnimatePresence>
        {(showLanding || showLandingByInactivity) && (
          <motion.div
            initial={{ opacity: showLanding ? 1 : 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: showLanding ? 0.5 : 1, 
              ease: showLanding ? "linear" : "easeInOut",
              delay: showLanding ? 0.8 : 0
            }}
            className="fixed inset-0 z-[100] cursor-pointer"
            onClick={handleLandingClick}
          >
            <Landing elementsVisible={landingElementsVisible} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* HomeQR Component - Modal con QR */}
      <AnimatePresence>
        {homeQRIsOpen && <HomeQR key="home_qr" />}
      </AnimatePresence>
      </div>
    </ErrorBoundary>
  );
}

export default App;